import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode, type RefObject } from "react";
import { useWindowId } from "@/components/ui/os-layout/windowIdContext";
import { aniNavRegistry, PAGE_NAV_SCOPE, type AniNavTargetKind } from "./aniNavRegistry";

const AniNavPathContext = createContext("");

function joinPath(base: string, id: string): string {
  return base ? `${base}/${id}` : id;
}

function useAniNavScopeKey(): string {
  return useWindowId() ?? PAGE_NAV_SCOPE;
}

/** Nest targets rendered inside under ``path`` (e.g. a tab's content under its tab id). */
export function AniNavScope({ path, children }: { path: string; children: ReactNode }) {
  const base = useContext(AniNavPathContext);
  return <AniNavPathContext.Provider value={joinPath(base, path)}>{children}</AniNavPathContext.Provider>;
}

type UseAniNavTargetOptions = {
  id: string;
  label: string;
  kind?: AniNavTargetKind;
  /** Defaults to clicking the element for `action` targets. */
  activate?: () => void;
  enabled?: boolean;
};

/**
 * Make an existing element showable by Floating Ani. Attach the returned ref to it.
 *
 * @example
 * const ref = useAniNavTarget<HTMLButtonElement>({ id: "lookthrough", label: "Lookthrough", kind: "action" });
 * <button ref={ref} onClick={toggle}>Lookthrough</button>
 */
export function useAniNavTarget<T extends HTMLElement>({
  id,
  label,
  kind = "section",
  activate,
  enabled = true,
}: UseAniNavTargetOptions): RefObject<T | null> {
  const scope = useAniNavScopeKey();
  const path = joinPath(useContext(AniNavPathContext), id);
  const elementRef = useRef<T | null>(null);
  const activateRef = useRef(activate);
  activateRef.current = activate;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    return aniNavRegistry.register(scope, {
      path,
      kind,
      label,
      getElement: () => elementRef.current,
      activate:
        kind === "section"
          ? undefined
          : () => (activateRef.current ? activateRef.current() : elementRef.current?.click()),
    });
  }, [scope, path, kind, label, enabled]);

  return elementRef;
}

/** Wrapper form of `useAniNavTarget` for sections. */
export function AniNavTarget({
  id,
  label,
  kind = "section",
  className,
  children,
}: {
  id: string;
  label: string;
  kind?: AniNavTargetKind;
  className?: string;
  children: ReactNode;
}) {
  const ref = useAniNavTarget<HTMLDivElement>({ id, label, kind });
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

/** Register every tab of a tab bar; used by `<Tabs aniNav>`. */
export function useAniNavTabs({
  enabled,
  tabs,
  activeTabId,
  onActivate,
  getElement,
}: {
  enabled: boolean;
  tabs: ReadonlyArray<{ id: string; label: string }>;
  activeTabId: string | undefined;
  onActivate: (tabId: string) => void;
  getElement: (tabId: string) => HTMLElement | null;
}): void {
  const scope = useAniNavScopeKey();
  const base = useContext(AniNavPathContext);
  const liveRef = useRef({ activeTabId, onActivate, getElement });
  liveRef.current = { activeTabId, onActivate, getElement };
  const tabKey = useMemo(() => tabs.map((tab) => `${tab.id}\0${tab.label}`).join("\n"), [tabs]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const unregisters = tabs.map((tab) =>
      aniNavRegistry.register(scope, {
        path: joinPath(base, tab.id),
        kind: "tab",
        label: tab.label,
        getElement: () => liveRef.current.getElement(tab.id),
        activate: () => liveRef.current.onActivate(tab.id),
        isActive: () => liveRef.current.activeTabId === tab.id,
      }),
    );
    return () => unregisters.forEach((unregister) => unregister());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tabKey captures tab ids/labels
  }, [enabled, scope, base, tabKey]);
}
